/**
 * Syncs the header's unread-messages badge + conversation caches from the
 * user-room UNREAD_COUNT socket event.
 *
 * Extracted from headerMenu (2026-07-21) so the REAL handler is testable —
 * the previous test re-implemented a mirror copy of this logic, which had
 * already drifted from the component.
 *
 * Wire shape: USER-room events arrive wrapped as { payload, sender } (see
 * the wire-quirk note in @gearghost/api-schemas/conversation). The payload
 * is validated with the shared UnreadCountPayloadSchema and FAILS CLOSED:
 * a drifted payload is dropped and logged — no badge update, no cache
 * invalidation — never half-applied.
 */
import { useEffect } from 'react'
import {
  CONVERSATION_SOCKET_EVENTS,
  UnreadCountPayloadSchema,
} from '@gearghost/api-schemas/conversation'
import { conversationApi } from '@/redux/api/conversationApi'
import { updateTotalUnreadMessages } from '@/redux/slices/socket'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { alertNewMessage } from '@/utils/messageAlerts'
import { createLogger } from '@/utils/logger'

const logger = createLogger('useUnreadCountSocketSync')

interface WrappedSocketEvent {
  payload?: unknown
}

/** The socket surface this hook needs (matches socket.io-client). */
interface SocketLike {
  on: (event: string, handler: (data: WrappedSocketEvent) => void) => void
  off: (event: string, handler: (data: WrappedSocketEvent) => void) => void
}

export const useUnreadCountSocketSync = (socket: SocketLike | null) => {
  const dispatch = useAppDispatch()
  const activeConversationId = useAppSelector(
    (state) => state.socket.activeConversationId,
  )

  useEffect(() => {
    if (!socket) return

    const onUnreadCount = (message: WrappedSocketEvent) => {
      const parsed = UnreadCountPayloadSchema.safeParse(message?.payload)
      if (!parsed.success) {
        logger.error(
          { issues: parsed.error.issues },
          'UNREAD_COUNT payload failed schema — dropped',
        )
        return
      }
      const { conversationId, unreadCount, totalUnreadMessages } = parsed.data
      dispatch(updateTotalUnreadMessages(totalUnreadMessages))
      // unreadCount > 0 means a genuinely new message (0 is a read-ack).
      // The open conversation stays silent — the user is already reading it.
      if (unreadCount > 0 && String(conversationId) !== activeConversationId) {
        alertNewMessage()
      }
      // Refresh conversations list so new conversations and last-message previews appear.
      // This is important because MESSAGE_RECEIVED is room-scoped — if the user hasn't
      // opened a conversation yet, they won't be in the room to receive it.
      // Only invalidate Messages when there are new unread messages (unreadCount > 0).
      // When unreadCount is 0 this is a mark-as-read acknowledgment — the message
      // content hasn't changed, and invalidating Messages would trigger the
      // MessageThread's markRead effect again, creating an infinite loop.
      // SupportUnread (the widget-launcher badge) rides along
      // UNCONDITIONALLY: the payload doesn't carry the conversation
      // type, admin replies bump the count, and mark-read acks
      // (unreadCount 0) are what CLEAR it — gating it on unreadCount
      // would leave a stale badge after reading the thread.
      const tags: (
        | { type: 'Conversations' | 'Messages'; id: string }
        | 'SupportUnread'
      )[] = [{ type: 'Conversations', id: 'LIST' }, 'SupportUnread']
      if (unreadCount > 0) {
        tags.push({ type: 'Messages', id: conversationId })
      }
      dispatch(conversationApi.util.invalidateTags(tags))
    }

    socket.on(CONVERSATION_SOCKET_EVENTS.UNREAD_COUNT, onUnreadCount)
    return () => {
      socket.off(CONVERSATION_SOCKET_EVENTS.UNREAD_COUNT, onUnreadCount)
    }
  }, [socket, dispatch, activeConversationId])
}
